import { AnyAction } from 'redux';
import cloneDeep from 'lodash/cloneDeep';
import { AddVertragToAngebote, GlobalActions } from '@ps20-frontend/shop-integration-redux';
import { App, getAnzahlVorschaeden, VwgVorbelegungdaten } from '@ps20-frontend/frontend-models';
import { TaasTarifrisikoschluesselVWG } from '@ps20/bff-models';
import { VorschaedenVisibility, VwgActions } from '../vwgActions';
import { BaujahrChanged } from '../action-types/baujahrChanged';
import { isVorschaedenVisibleByValue } from '../../app/shared/selectors/gebaeude.selector';
import { IAppState } from '../appState';

export interface Vorschaden {
  count: number;
  includeInTarifierung: boolean;
  includeInDruck: boolean;
  includeInEinreichen: boolean;
  visibleInErsatzgeschaeft: boolean;
  countFromRisikoortService?: number;
}

export interface Vorschaeden {
  vorhanden: boolean;
  feuerVorschaden: Vorschaden;
  leitungswasserVorschaden: Vorschaden;
  sturmhagelVorschaden: Vorschaden;
  elementarVorschaden: Vorschaden;
}

function resetVorschaeden(newState: Vorschaeden): Vorschaeden {
  newState.feuerVorschaden.count = 0;
  newState.elementarVorschaden.count = 0;
  newState.leitungswasserVorschaden.count = 0;
  newState.sturmhagelVorschaden.count = 0;
  return newState;
}

export const vorschaedenReducer = function (
  sectionState: Vorschaeden = null,
  action: AnyAction,
  appState: IAppState,
): Vorschaeden {
  switch (action.type) {
    case VwgActions.VORSCHAEDEN_CHANGED: {
      const newState = resetVorschaeden(cloneDeep(sectionState));
      return {
        ...newState,
        vorhanden: action.payload.vorschaeden,
      };
    }
    case VwgActions.FEUER_CHANGED: {
      return {
        ...sectionState,
        feuerVorschaden: {
          ...sectionState.feuerVorschaden,
          count: action.payload.feuer,
        },
      };
    }
    case VwgActions.LEITUNGSWASSER_CHANGED: {
      return {
        ...sectionState,
        leitungswasserVorschaden: {
          ...sectionState.leitungswasserVorschaden,
          count: action.payload.leitungswasser,
        },
      };
    }
    case VwgActions.STURMHAGEL_CHANGED: {
      return {
        ...sectionState,
        sturmhagelVorschaden: {
          ...sectionState.sturmhagelVorschaden,
          count: action.payload.sturmhagel,
        },
      };
    }
    case VwgActions.ELEMENTAR_CHANGED: {
      return {
        ...sectionState,
        elementarVorschaden: {
          ...sectionState.elementarVorschaden,
          count: action.payload.elementar,
        },
      };
    }
    case VwgActions.BAUJAHR_CHANGED: {
      const actionPayload: BaujahrChanged = action.payload;
      if (!isVorschaedenVisibleByValue(actionPayload.baujahr)) {
        return {
          vorhanden: null,
          elementarVorschaden: {
            ...sectionState.elementarVorschaden,
            count: 0,
            visibleInErsatzgeschaeft: false,
          },
          feuerVorschaden: {
            ...sectionState.feuerVorschaden,
            count: 0,
            visibleInErsatzgeschaeft: false,
          },
          leitungswasserVorschaden: {
            ...sectionState.leitungswasserVorschaden,
            count: 0,
            visibleInErsatzgeschaeft: false,
          },
          sturmhagelVorschaden: {
            ...sectionState.sturmhagelVorschaden,
            count: 0,
            visibleInErsatzgeschaeft: false,
          },
        };
      }
      return sectionState;
    }
    case GlobalActions.ADD_VORVERTRAG_TO_ANGEBOTE: {
      const actionPayload: AddVertragToAngebote = action.payload;
      const newState: Vorschaeden = cloneDeep(sectionState);
      if (
        actionPayload.appId === App.VWG &&
        actionPayload.angebotsId === appState.angebotsId &&
        action.payload.vorvertragsNummern
      ) {
        newState.vorhanden = false;
        resetVorschaeden(newState);
        return newState;
      }
      return sectionState;
    }
    case GlobalActions.VORBELEGUNG_FOR_BEIBEHALTUNG_REQUESTED: {
      const actionPayload: VwgVorbelegungdaten = action.payload;
      if (actionPayload.forApp === App.VWG && actionPayload.angebotsId === appState.angebotsId) {
        const anzVorschaedenFeuer = getAnzahlVorschaeden(
          actionPayload.vorvertragBausteinList,
          TaasTarifrisikoschluesselVWG.FEUER,
        );
        const anzVorschaedenLw = getAnzahlVorschaeden(
          actionPayload.vorvertragBausteinList,
          TaasTarifrisikoschluesselVWG.LEITUNGSWASSER,
        );
        const anzVorschaedenSt = getAnzahlVorschaeden(
          actionPayload.vorvertragBausteinList,
          TaasTarifrisikoschluesselVWG.STURM,
        );
        const anzVorschaedenEle = getAnzahlVorschaeden(
          actionPayload.vorvertragBausteinList,
          TaasTarifrisikoschluesselVWG.ELEMENTAR,
        );
        return {
          ...sectionState,
          vorhanden:
            anzVorschaedenFeuer > 0 ||
            anzVorschaedenLw > 0 ||
            anzVorschaedenSt > 0 ||
            anzVorschaedenEle > 0,
          feuerVorschaden: {
            ...sectionState.feuerVorschaden,
            count: anzVorschaedenFeuer,
          },
          leitungswasserVorschaden: {
            ...sectionState.elementarVorschaden,
            count: anzVorschaedenLw,
          },
          sturmhagelVorschaden: {
            ...sectionState.elementarVorschaden,
            count: anzVorschaedenSt,
          },
          elementarVorschaden: {
            ...sectionState.elementarVorschaden,
            count: anzVorschaedenEle,
          },
        };
      }
      return sectionState;
    }
    case VwgActions.EXCLUDE_ELEMENTAR_VORSCHAEDEN: {
      return {
        ...sectionState,
        elementarVorschaden: {
          ...sectionState.elementarVorschaden,
          includeInDruck: false,
          includeInTarifierung: false,
          includeInEinreichen: false,
        },
      };
    }
    case VwgActions.INCLUDE_ELEMENTAR_VORSCHAEDEN: {
      return {
        ...sectionState,
        elementarVorschaden: {
          ...sectionState.elementarVorschaden,
          includeInDruck: true,
          includeInTarifierung: true,
          includeInEinreichen: true,
        },
      };
    }
    case VwgActions.SHOW_VORSCHAEDEN_ERSATZGESCHAEFT: {
      const payload = action.payload as VorschaedenVisibility;
      return {
        ...sectionState,
        elementarVorschaden: {
          ...sectionState.elementarVorschaden,
          visibleInErsatzgeschaeft:
            payload.elementar ?? sectionState.elementarVorschaden.visibleInErsatzgeschaeft,
        },
        feuerVorschaden: {
          ...sectionState.feuerVorschaden,
          visibleInErsatzgeschaeft:
            payload.feuer ?? sectionState.feuerVorschaden.visibleInErsatzgeschaeft,
        },
        sturmhagelVorschaden: {
          ...sectionState.sturmhagelVorschaden,
          visibleInErsatzgeschaeft:
            payload.sturmhagel ?? sectionState.sturmhagelVorschaden.visibleInErsatzgeschaeft,
        },
        leitungswasserVorschaden: {
          ...sectionState.leitungswasserVorschaden,
          visibleInErsatzgeschaeft:
            payload.leitungswasser ??
            sectionState.leitungswasserVorschaden.visibleInErsatzgeschaeft,
        },
      };
    }
    case VwgActions.UPDATE_ELEMENTAR_VORSCHADEN_RISIKOORT: {
      return {
        ...sectionState,
        elementarVorschaden: {
          ...sectionState.elementarVorschaden,
          countFromRisikoortService: action.payload,
        },
      };
    }
    default:
      return sectionState;
  }
};
